import { Link } from 'react-router';
import { Kanban, Workflow, type LucideIcon } from 'lucide-react';
import { useSession } from '@/contexts/SessionContext';
import { isActivePersona, type FrontendKey } from '@septima/contract';
import { FRONTENDS, sectionForPathname, visibleSectionsFor } from '@/lib/frontends';
import { usePathname } from '@/lib/navigation';
import { useReviewQueueCounts } from '@/lib/use-review-queue-counts';
import HistoryNavButtons from './HistoryNavButtons';
import { PageToolbarFrame, toolbarHeading, usePageToolbarHost } from '@/components/ui/PageToolbar';

interface StaffPageToolbarProps {
    frontend: FrontendKey;
}

// Make a readable label from the first path segment after `/<frontend>/`, so
// a route with no sidebar row still gets a heading (for example
// `/view/fund-management` gives "Fund Management").
function humanisePathLabel(pathname: string): string | null {
    const segment = pathname.replace(/^\/[^/]+\/?/, '').split('/')[0];
    if (!segment) return null;
    return segment
        .split('-')
        .map((word) => (word ? word.charAt(0).toUpperCase() + word.slice(1) : word))
        .join(' ');
}

/** A 24px icon link of the strip, with a tooltip on hover and on focus. */
function ToolbarIconLink({
    to,
    label,
    icon: Icon,
    count,
}: {
    to: string;
    label: string;
    icon: LucideIcon;
    count?: number;
}) {
    const hasCount = count !== undefined && count > 0;
    const name = hasCount ? `${label}, ${count} waiting for review` : label;
    return (
        <Link
            to={to}
            aria-label={name}
            className="group relative grid h-6 w-6 place-items-center rounded-button text-[rgb(var(--color-text-secondary))] transition-colors hover:bg-[rgb(var(--color-sidebar-hover)/0.06)] hover:text-[rgb(var(--color-text-primary))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--color-accent)/0.28)] pointer-coarse:h-8 pointer-coarse:w-8"
        >
            <Icon className="h-4 w-4" aria-hidden="true" />
            {hasCount && (
                <span
                    aria-hidden="true"
                    className="absolute -right-1 -top-1 min-w-[14px] rounded-full bg-[rgb(var(--color-accent))] px-[3px] text-center font-mono text-[9.5px] leading-[14px] tabular-nums text-white"
                >
                    {count > 99 ? '99+' : count}
                </span>
            )}
            <span
                role="tooltip"
                aria-hidden="true"
                className="pointer-events-none absolute right-0 top-full z-50 mt-1 hidden whitespace-nowrap rounded-[6px] border border-[rgb(var(--color-divider))] bg-[rgb(var(--color-surface-raised))] px-2 py-1 text-[12px] font-medium leading-4 text-[rgb(var(--color-text-primary))] shadow-md group-hover:block group-focus-visible:block"
            >
                {name}
            </span>
        </Link>
    );
}

// The navigation bar and the page bar of the staff shell. They show at every
// width: below `lg` they sit under `MobileTopBar` and wrap their controls. The
// page gives the title and the controls through `<PageToolbar>` or
// `<PageHeader>`; a page that gives none gets the label of its sidebar section
// as the h1, and no page bar.
export default function StaffPageToolbar({ frontend }: StaffPageToolbarProps) {
    const host = usePageToolbarHost();
    const pathname = usePathname();
    const { user } = useSession();
    // The Agents kanban holds the review column, so the count of pending
    // reviews goes on the Agents button.
    const { itemCount } = useReviewQueueCounts(frontend);

    const section = sectionForPathname(frontend, pathname);
    const sectionIsRoot = section !== null && pathname.replace(/\/$/, '') === section.href;
    // A route with no sidebar section gets a heading from its first path
    // segment, but no crumb. That segment is not always a route (for example
    // `/desk/deviation/:id` has no `/desk/deviation` page), so a link to it
    // could lead to a page that does not exist.
    const pathLabel = humanisePathLabel(pathname);
    const { crumbs, title } = toolbarHeading({
        viewLabel: FRONTENDS[frontend].shortLabel,
        viewHref: `/${frontend}`,
        sectionLabel: section?.label ?? pathLabel,
        sectionHref: section?.href ?? null,
        sectionIsRoot,
        pageTitle: host?.active?.title,
        trail: host?.active?.trail,
        sectionOverride: host?.active?.section,
    });

    // Show a global button only when the active persona can see its page.
    // This uses the same policy as the sidebar (ADR-0004), so a button never
    // links a persona to a page that the backend refuses.
    const activePersona =
        user?.activePersona && isActivePersona(user.activePersona) ? user.activePersona : frontend;
    const visibleSections = visibleSectionsFor(activePersona, user?.personas ?? []);
    const agentsSection = visibleSections.find((s) => s.key === 'agents');
    const workflowsSection = visibleSections.find((s) => s.key === 'workflows');

    const trailing =
        agentsSection || workflowsSection ? (
            <>
                {agentsSection && (
                    <ToolbarIconLink
                        to={agentsSection.href}
                        label="Agents"
                        icon={Kanban}
                        count={itemCount}
                    />
                )}
                {workflowsSection && (
                    <ToolbarIconLink to={workflowsSection.href} label="Workflows" icon={Workflow} />
                )}
            </>
        ) : null;

    return (
        <PageToolbarFrame
            crumbs={crumbs}
            title={title}
            subtitle={host?.active?.subtitle}
            leading={<HistoryNavButtons />}
            trailing={trailing}
            showPageBar={host?.active?.hasPageBar ?? false}
            startRef={host?.setStartEl}
            endRef={host?.setEndEl}
        />
    );
}
